/**
 * AI Command Center — the main wow-screen.
 *
 * Three columns:
 *   - left:  task input + plan steps timeline
 *   - middle: result of the latest step (search results, etc.)
 *   - right:  agent activity feed (recent runs)
 *
 * Polling-based for Sprint 5; ws-gateway swap in Sprint 6.
 */

import { useCallback, useRef, useState } from "react";
import { Send, Sparkles, Wifi, WifiOff } from "lucide-react";
import { Badge, Button, Card, CardHeader, Input, cn } from "@vseh/ui";
import {
  AgentBubble,
  AgentStatus,
  type AgentStatusValue,
  UserBubble,
} from "@vseh/ai-components";

import { dispatcherApi, type PlanDto, type PlanStepDto } from "../shared/api";
import {
  useFetch,
  usePolling,
  useEventStream,
  type EventEnvelope,
  type ConnectionState,
} from "../shared/hooks";
import { agentRunsApi, type AgentRunDto } from "../shared/api";
import { useT } from "../shared/i18n";
import { ErrorBlock, LoadingBlock } from "../shared/components/States";


// Sample keys; resolved per-render through useT so a Russian
// deployment shows Russian samples and an English one shows English.
const SAMPLE_KEYS = [
  "command.sample.1",
  "command.sample.2",
  "command.sample.3",
  "command.sample.4",
] as const;


export function CommandCenter(): JSX.Element {
  const [taskText, setTaskText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeCorrelationId, setActiveCorrelationId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<Error | null>(null);

  const submit = useCallback(async (text: string) => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await dispatcherApi.postTask(text);
      setActiveCorrelationId(r.correlation_id);
      setTaskText("");
    } catch (e) {
      setSubmitError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setSubmitting(false);
    }
  }, [submitting]);

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-7 flex flex-col gap-4">
        <TaskInputCard
          value={taskText}
          onChange={setTaskText}
          onSubmit={() => submit(taskText)}
          submitting={submitting}
          submitError={submitError}
          onSample={(s) => submit(s)}
        />
        {activeCorrelationId && (
          <PlanProgressCard correlationId={activeCorrelationId} />
        )}
      </div>
      <div className="col-span-5">
        <AgentActivityCard />
      </div>
    </div>
  );
}


// =============================================================================
// Task input
// =============================================================================


interface TaskInputProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onSample: (s: string) => void;
  submitting: boolean;
  submitError: Error | null;
}

function TaskInputCard(props: TaskInputProps): JSX.Element {
  const t = useT();
  return (
    <Card>
      <CardHeader
        title={t("cc.title")}
        description={t("cc.subtitle")}
      />
      <div className="space-y-3">
        <Input
          placeholder={t("cc.placeholder")}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              props.onSubmit();
            }
          }}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ai"
            leftIcon={<Send className="size-4" />}
            loading={props.submitting}
            disabled={props.value.trim().length === 0}
            onClick={props.onSubmit}
          >
            {t("cc.submit")}
          </Button>
          <span className="text-xs text-base-500">{t("cc.samples_hint")}</span>
          {SAMPLE_KEYS.map((k) => {
            const s = t(k);
            return (
              <button
                key={k}
                type="button"
                className="rounded-sm border border-base-200 bg-base-50 px-2 py-1 text-2xs text-base-700 hover:bg-base-100"
                onClick={() => props.onSample(s)}
              >
                {s}
              </button>
            );
          })}
        </div>
        {props.submitError && (
          <ErrorBlock error={props.submitError} />
        )}
      </div>
    </Card>
  );
}


// =============================================================================
// Plan progress (polling)
// =============================================================================


function PlanProgressCard({ correlationId }: { correlationId: string }): JSX.Element {
  const t = useT();
  const { data: plan, error, loading } = usePolling<PlanDto>(
    correlationId,
    () => dispatcherApi.getPlanByCorrelation(correlationId),
    (p) => p.status === "completed" || p.status === "failed",
    { intervalMs: 700, timeoutMs: 30_000 },
  );

  return (
    <Card>
      <CardHeader
        title={t("cc.plan.title")}
        description={
          <span className="font-mono text-xs">{correlationId}</span>
        }
        actions={plan ? <PlanStatusBadge status={plan.status} /> : null}
      />

      {loading && !plan && <LoadingBlock label={t("cc.plan.loading")} />}
      {error && <ErrorBlock error={error} />}

      {plan && (
        <div className="space-y-4">
          <UserBubble timestamp={new Date(plan.created_at).toLocaleTimeString("ru-RU")}>
            {plan.goal_text}
          </UserBubble>

          <AgentBubble agentLabel="AI Dispatcher">
            <AgentStatus
              status={planStatusToAgentStatus(plan.status)}
              label={`${t("cc.plan.intent_label")}: ${intentLabel(plan.intent, t)}`}
            />
            <ol className="mt-3 space-y-2">
              {plan.steps.map((step, idx) => (
                <li key={step.step_id} className="rounded-md border border-base-200 bg-base-50 p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-base-700">
                      {idx + 1}. {step.kind}
                    </span>
                    <StepStatusBadge status={step.status} />
                  </div>
                  {step.status === "completed" && step.result && (
                    <StepResult kind={step.kind} result={step.result} />
                  )}
                  {step.error_message && (
                    <div className="mt-1 text-xs text-danger-700">{step.error_message}</div>
                  )}
                </li>
              ))}
            </ol>

            {plan.final_response && (
              <div className="mt-3 rounded-md border-l-2 border-success-500 bg-success-50 p-3 text-sm text-base-900">
                <div className="mb-1 text-2xs font-medium uppercase tracking-wide text-success-700">
                  {t("command.final_answer")}
                </div>
                <pre className="whitespace-pre-wrap font-sans text-md">{plan.final_response}</pre>
              </div>
            )}
          </AgentBubble>
        </div>
      )}
    </Card>
  );
}


function PlanStatusBadge({ status }: { status: PlanDto["status"] }): JSX.Element {
  const tone = {
    pending:   "neutral" as const,
    running:   "ai"      as const,
    completed: "success" as const,
    failed:    "danger"  as const,
  }[status];
  return <Badge tone={tone} dot>{status}</Badge>;
}


function StepStatusBadge({ status }: { status: PlanStepDto["status"] }): JSX.Element {
  const tone = {
    pending:   "neutral" as const,
    running:   "ai"      as const,
    completed: "success" as const,
    failed:    "danger"  as const,
    skipped:   "neutral" as const,
  }[status];
  return <Badge tone={tone}>{status}</Badge>;
}


function planStatusToAgentStatus(s: PlanDto["status"]): AgentStatusValue {
  switch (s) {
    case "pending":   return "thinking";
    case "running":   return "acting";
    case "completed": return "completed";
    case "failed":    return "failed";
  }
}


// Maps intent ids to dictionary keys. The actual translation happens
// in the component scope where `t()` is in reach; pass `t` in
// because module-level functions can't call hooks.
const INTENT_KEY: Record<string, string> = {
  rent_equipment:    "cc.intent.rent_equipment",
  price_inquiry:     "cc.intent.price_inquiry",
  publish_listing:   "cc.intent.publish_listing",
  support_question:  "cc.intent.support_question",
  unclear:           "cc.intent.unclear",
};

function intentLabel(intent: string, t: (k: string) => string): string {
  const key = INTENT_KEY[intent];
  return key ? t(key) : intent;
}


// =============================================================================
// Step result rendering
// =============================================================================


function StepResult({ kind, result }: { kind: string; result: Record<string, unknown> }): JSX.Element {
  const t = useT();
  if (kind === "search.equipment") {
    const total = (result.total_matched as number | undefined) ?? 0;
    const items = (result.results as Array<Record<string, unknown>> | undefined) ?? [];
    return (
      <div className="mt-2 space-y-2">
        <div className="text-xs text-base-600">
          {t("command.found", { total, shown: items.length })}
        </div>
        <ol className="space-y-1.5">
          {items.slice(0, 5).map((item) => (
            <SearchResultRow key={String(item.listing_id)} item={item} />
          ))}
        </ol>
      </div>
    );
  }

  // Generic dump for other step kinds.
  return (
    <pre className="mt-2 overflow-x-auto rounded-sm bg-base-900 p-2 text-2xs text-base-100">
      {JSON.stringify(result, null, 2)}
    </pre>
  );
}


function SearchResultRow({ item }: { item: Record<string, unknown> }): JSX.Element {
  const score    = (item.score as number | undefined) ?? 0;
  const breakdown = (item.score_breakdown as Record<string, number> | undefined) ?? {};
  return (
    <li className="flex items-start justify-between gap-3 rounded-sm bg-surface px-2 py-1.5">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-base-900">{String(item.title)}</div>
        <div className="mt-0.5 flex items-center gap-2 text-2xs text-base-500">
          {Object.entries(breakdown).map(([k, v]) => (
            <span key={k} className="font-mono tabular-nums">
              {k}: {Number(v).toFixed(2)}
            </span>
          ))}
        </div>
      </div>
      <Badge tone="ai">
        <span className="font-mono tabular-nums">{score.toFixed(3)}</span>
      </Badge>
    </li>
  );
}


// =============================================================================
// Agent activity feed (right column)
//
// Sprint 6: live event stream from ws-gateway. We render two stacked
// blocks — a "live" tape that shows raw NATS events as they arrive
// (great for the demo / proof that realtime works), and a "history"
// list of agent runs from REST for context on completed work.
// =============================================================================


/** How many recent live events we keep in memory before evicting. */
const LIVE_EVENT_BUFFER = 30;


interface LiveEvent {
  /** Local-receive id, used as React key — not the producer event_id. */
  receivedId: string;
  receivedAt: number;
  envelope:   EventEnvelope;
}


function AgentActivityCard(): JSX.Element {
  const t = useT();
  const [liveEvents, setLiveEvents] = useState<LiveEvent[]>([]);
  const counterRef = useRef(0);

  const handleEvent = useCallback((envelope: EventEnvelope) => {
    counterRef.current += 1;
    const id = `${Date.now()}-${counterRef.current}`;
    setLiveEvents((prev) => {
      const next = [{ receivedId: id, receivedAt: Date.now(), envelope }, ...prev];
      return next.slice(0, LIVE_EVENT_BUFFER);
    });
  }, []);

  const stream = useEventStream(handleEvent);

  const { data: runs, error, loading, refetch } = useFetch<AgentRunDto[]>(
    (signal) => agentRunsApi.list({ limit: 10 }, { signal }),
    [],
  );

  return (
    <Card>
      <CardHeader
        title={t("cc.activity.title")}
        description={t("cc.activity.subtitle")}
        actions={<ConnectionBadge state={stream.state} />}
      />

      <div className="space-y-4">
        <LiveEventsBlock events={liveEvents} state={stream.state} />

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-2xs font-medium uppercase tracking-wide text-base-500">
              {t("cc.activity.history")}
            </span>
            <Button variant="ghost" size="xs" onClick={refetch}>{t("common.refresh")}</Button>
          </div>

          {loading && !runs && <LoadingBlock />}
          {error && <ErrorBlock error={error} onRetry={refetch} />}

          {runs && runs.length === 0 && (
            <div className="py-4 text-center text-sm text-base-500">
              {t("cc.activity.empty")}
            </div>
          )}

          {runs && runs.length > 0 && <RunsList runs={runs} />}
        </div>
      </div>
    </Card>
  );
}


function ConnectionBadge({ state }: { state: ConnectionState }): JSX.Element {
  if (state === "open") {
    return (
      <Badge tone="success" dot>
        <Wifi aria-hidden="true" className="mr-1 size-3" />
        live
      </Badge>
    );
  }
  if (state === "connecting") {
    return <Badge tone="ai" dot>connecting…</Badge>;
  }
  return (
    <Badge tone="neutral">
      <WifiOff aria-hidden="true" className="mr-1 size-3" />
      {state}
    </Badge>
  );
}


function LiveEventsBlock(
  { events, state }: { events: LiveEvent[]; state: ConnectionState },
): JSX.Element {
  const t = useT();
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-2xs font-medium uppercase tracking-wide text-base-500">
          {t("cc.activity.feed")}
        </span>
        <span className="text-2xs text-base-500 tabular-nums">{events.length}</span>
      </div>
      {events.length === 0 ? (
        <div className="rounded-md border border-dashed border-base-200 px-3 py-4 text-center text-xs text-base-500">
          {state === "open"
            ? t("cc.feed.waiting")
            : t("cc.feed.offline")}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {events.map((ev) => (
            <LiveEventRow key={ev.receivedId} event={ev} />
          ))}
        </ul>
      )}
    </div>
  );
}


function LiveEventRow({ event }: { event: LiveEvent }): JSX.Element {
  const tone = eventTone(event.envelope.event_type);
  return (
    <li className="flex items-start gap-2 rounded-sm bg-base-50 px-2 py-1.5 text-xs">
      <span
        className={cn(
          "mt-1 size-1.5 shrink-0 rounded-full animate-agent-pulse",
          tone === "success" ? "bg-success-500" :
          tone === "danger"  ? "bg-danger-500"  :
                               "bg-ai-500",
        )}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <span className="font-mono text-xs text-base-800">{event.envelope.event_type}</span>
        {event.envelope.correlation_id && (
          <span className="ml-2 truncate font-mono text-2xs text-base-500">
            {event.envelope.correlation_id.slice(0, 8)}
          </span>
        )}
      </div>
      <span className="text-2xs text-base-500 tabular-nums">
        {new Date(event.receivedAt).toLocaleTimeString("ru-RU")}
      </span>
    </li>
  );
}


function eventTone(eventType: string): "success" | "danger" | "ai" {
  if (eventType.endsWith(".failed") || eventType.includes(".rejected")) return "danger";
  if (eventType.endsWith(".completed") || eventType.endsWith(".published")) return "success";
  return "ai";
}


function RunsList({ runs }: { runs: AgentRunDto[] }): JSX.Element {
  return (
    <ul className="space-y-2.5">
      {runs.map((run) => (
        <li key={run.id} className="flex items-start gap-3 text-sm">
          <span
            className={cn(
              "mt-1 size-2 shrink-0 rounded-full",
              run.status === "completed" ? "bg-success-500" :
              run.status === "failed"    ? "bg-danger-500"  :
              run.status === "running"   ? "bg-ai-500 animate-agent-pulse" :
                                            "bg-base-400",
            )}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-xs text-base-700">{run.agent_id}</span>
              <span className="text-2xs text-base-500">
                {new Date(run.started_at).toLocaleTimeString("ru-RU")}
              </span>
            </div>
            <div className="text-2xs text-base-500">
              {run.trigger_type}
              {run.duration_ms != null && (
                <span className="ml-2 tabular-nums">{(run.duration_ms / 1000).toFixed(1)}s</span>
              )}
              {run.total_cost_usd > 0 && (
                <span className="ml-2 tabular-nums">${run.total_cost_usd.toFixed(4)}</span>
              )}
            </div>
          </div>
          <Sparkles aria-hidden="true" className="size-3 text-ai-500" />
        </li>
      ))}
    </ul>
  );
}
